import type { CanonBundleV1, EngineRunReport } from './canonBundle';

export type TauriInvoke = <T>(command: string, args?: Record<string, unknown>) => Promise<T>;
export type TauriListen = <T>(event: string, handler: (event: { payload: T }) => void) => Promise<() => void>;

async function defaultInvoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke<T>(command, args);
}

export async function invokeTauri<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  return defaultInvoke<T>(command, args);
}

export async function listenTauri<T>(event: string, handler: (event: { payload: T }) => void): Promise<() => void> {
  const { listen } = await import('@tauri-apps/api/event');
  return listen<T>(event, handler);
}

export function isTauriRuntime(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window;
}

export async function runCanonBundleThroughTauri(
  bundle: CanonBundleV1,
  invoke: TauriInvoke = defaultInvoke,
): Promise<EngineRunReport> {
  return invoke<EngineRunReport>('run_canon_bundle', {
    bundleJson: JSON.stringify(bundle),
  });
}

// ── AI-native K0: canon 미러(Dexie 정본 → 단방향 읽기전용) — 에이전트의 canon 표면 ──
export interface MirrorManifest {
  schemaVersion: string;
  projectId: string;
  projectTitle: string;
  bundleHash: string;
  bundleFile: string;
  mirroredAt: number;
  beats: number;
  actors: number;
  institutions: number;
  resources: number;
  locations: number;
  scopes: { id: string; label: string; order: number; facts: number }[];
}

/** 현재 canon 번들을 미러에 기록(원자쓰기·0600). 에이전트 read 도구가 이걸 읽는다. */
export async function mirrorCanonThroughTauri(
  bundle: CanonBundleV1,
  bundleHash: string,
  invoke: TauriInvoke = defaultInvoke,
): Promise<MirrorManifest> {
  return invoke<MirrorManifest>('mirror_canon', {
    bundleJson: JSON.stringify(bundle),
    bundleHash,
  });
}

// ── 역추출(창작 OS v2 P0): 산문 → 타입드 주장 후보(암묵 전제 발굴) + 세계의 반응 ──
export interface ExtractedCandidate {
  claim: string;
  references_fact: string | null;
  references_entity: string | null;
  implicit: boolean;
  rationale: string;
  confidence: number;
}

export interface ExtractWorldResponse {
  subject: string;
  kind: string;
  evidence: string;
  canon_refs: string[];
}

export interface BeatExtractResult {
  world: string;
  time_scope: string;
  candidates: ExtractedCandidate[];
  responses: ExtractWorldResponse[];
  character_os: string[]; // 추출에 주입된 WorldForge 캐릭터 OS(이름들)
}

export interface ExtractBundleBeatContext {
  beatId?: string;
  timeScope?: string;
  refs?: string[];
}

/** 비트 본문(산문)을 Rust 엔진이 타입드 후보로 구조화. 전부 proposed — 작가가 accept해야 canon 입력. */
export async function extractBundleBeatThroughTauri(
  bundle: CanonBundleV1,
  beatBody: string,
  contextOrInvoke: ExtractBundleBeatContext | TauriInvoke = {},
  maybeInvoke: TauriInvoke = defaultInvoke,
): Promise<BeatExtractResult> {
  const context = typeof contextOrInvoke === 'function' ? {} : contextOrInvoke;
  const invoke = typeof contextOrInvoke === 'function' ? contextOrInvoke : maybeInvoke;
  return invoke<BeatExtractResult>('extract_bundle_beat', {
    bundleJson: JSON.stringify(bundle),
    beatBody,
    ...(context.beatId ? { beatId: context.beatId } : {}),
    ...(context.timeScope ? { timeScope: context.timeScope } : {}),
    ...(context.refs ? { refs: context.refs } : {}),
  });
}

// ── 정방향 생성(창작 OS v1 P1): 직전 산문 → 다음 장면 후보(작가 보이스·canon 근거, 전부 proposed) ──
export interface GeneratedBeatDraft {
  title: string;
  body: string;
  rationale: string;
  stop_reason?: AutowriteStop | null;
}

export interface GenerateNextResult {
  drafts: GeneratedBeatDraft[];
  character_os: string[]; // 생성에 주입된 WorldForge 캐릭터 OS(이름들)
}

/** 직전 산문을 번들 canon + 캐릭터 OS(reveal_budget)에 근거해 다음 장면 후보로. AI는 제안, 작가가 고른다(자동 적용 0). */
export async function generateBundleNextThroughTauri(
  bundle: CanonBundleV1,
  recentProse: string,
  n = 2,
  refsOrInvoke: string[] | TauriInvoke = [],
  maybeInvoke: TauriInvoke = defaultInvoke,
): Promise<GenerateNextResult> {
  const refs = typeof refsOrInvoke === 'function' ? [] : refsOrInvoke;
  const invoke = typeof refsOrInvoke === 'function' ? refsOrInvoke : maybeInvoke;
  return invoke<GenerateNextResult>('generate_bundle_next', {
    bundleJson: JSON.stringify(bundle),
    recentProse,
    n,
    ...(refs.length ? { refs } : {}),
  });
}

// ── 자동집필 한 회전(M5): 직전 산문 → 다음 장면 후보 N개 + 후보별 봉인/정합 검수(pass/stop). 전부 proposed ──
export type AutowriteStop =
  | 'HardViolation' // 봉합 불가 hard(봉인/정합 위반) — 후보 폐기
  | 'NeedsRewriteProposal' // 작가 재작성 필요
  | 'RepairPreviewNonZero' // green 되려면 의미를 버려야(assert 제거)
  | 'EmptyExtraction' // 추출 0 — 검증 불가(확인 필요)
  | 'ExtractorLowConfidence' // 최고 신뢰<0.6 — 검증 불확실(확인 필요)
  | 'CharacterOsForbiddenReveal' // 캐릭터 OS 금지 폭로가 산문에 직접 누설됨
  | 'CharacterOsRevealBudgetZero' // 캐릭터 OS reveal_budget 0.0 라벨이 산문에 직접 누설됨
  | 'CharacterOsRevealBudgetExceeded'; // 캐릭터 OS reveal_budget trigger cost가 예산을 초과함

export interface AutowriteDraftVerdict {
  draft: GeneratedBeatDraft;
  draft_hash: string; // 재생성 반복(RepeatedFailure) 감지용
  candidates: ExtractedCandidate[]; // 역추출(암묵 전제)
  consistency: EngineRunReport; // probe 주입 결정론 검수(hardBefore·violations·proposals)
  pass: boolean; // hard_before==0 && 수정제안 0
  stop_reason: AutowriteStop | null;
}

export interface AutowriteAttempt {
  target_scope: string; // 검수 기준 part(봉인 scope)
  verdicts: AutowriteDraftVerdict[];
  character_os: string[];
}

/** 자동집필 한 회전. 직전 산문 → 다음 장면 후보 + 각 후보 봉인/정합 검수. AI=제안, 작가가 accept(이어쓰기)해야 canon. */
export async function autowriteLoopThroughTauri(
  bundle: CanonBundleV1,
  recentProse: string,
  n = 2,
  refs?: string[],
  invoke: TauriInvoke = defaultInvoke,
): Promise<AutowriteAttempt> {
  return invoke<AutowriteAttempt>('autowrite_loop', {
    bundleJson: JSON.stringify(bundle),
    recentProse,
    n,
    ...(refs ? { refs } : {}),
  });
}

// ── 분기 탐색(결과 엔진): 가설 선택(Δ)을 canon에 돌려 파급·분기점 계산. 분기는 전부 가설(작가가 고른다) ──
export interface BranchProbeAssert {
  claim: string;
  references_fact?: string | null;
  references_entity?: string | null;
}

export interface BranchMutation {
  kind: 'set_fact' | 'clear_fact' | 'collapse_resource' | 'uncollapse_resource' | 'set_institution_status';
  scope?: string;
  fact?: string;
  resource?: string;
  institution?: string;
  status?: string;
}

export interface BranchChoice {
  id: string;
  label: string;
  mutations?: BranchMutation[];
  probe?: BranchProbeAssert[];
}

export interface BranchSpecInput {
  base_scope: string;
  probe?: BranchProbeAssert[];
  choices?: BranchChoice[];
}

export interface BranchViolation {
  kind: string;
  severity: string;
  evidence: string;
  canon_refs: string[];
}

export interface BranchOutcome {
  id: string;
  label: string;
  verdict: string;
  native_hard: number;
  viable: boolean;
  forced_rewrite: boolean;
  removed_claims: string[];
  ripple: BranchViolation[];
  writer_held: string[];
}

export interface BranchReport {
  base_scope: string;
  is_branch_point: boolean;
  viable_count: number;
  outcomes: BranchOutcome[];
}

/** 분기 탐색. recommend=true면 choices가 비었을 때 proposer가 분기 후보를 제안(하이브리드의 AI측). */
export async function branchExploreThroughTauri(
  bundle: CanonBundleV1,
  spec: BranchSpecInput,
  recommend = false,
  invoke: TauriInvoke = defaultInvoke,
): Promise<BranchReport> {
  return invoke<BranchReport>('branch_explore', {
    bundleJson: JSON.stringify(bundle),
    specJson: JSON.stringify(spec),
    recommend,
  });
}

export interface GeminiKeyStatus {
  service: string;
  source: 'keychain';
  available: boolean;
  message: string;
}

/** 데스크톱 Keychain 상태 확인. 비밀값은 반환하지 않는다. */
export async function geminiKeyStatusThroughTauri(
  invoke: TauriInvoke = defaultInvoke,
): Promise<GeminiKeyStatus> {
  return invoke<GeminiKeyStatus>('gemini_key_status');
}

export interface Part1ReadthroughStatus {
  packetExists: boolean;
  packetCurrent: boolean;
  approvalExists: boolean;
  approvalApproved: boolean;
  approvalCurrent: boolean;
  packetPath: string;
  approvalPath: string;
  manuscriptSha256?: string | null;
  packetSha256?: string | null;
  error?: string | null;
}

/** 1부 통독 패킷/작가 승인 상태. 읽기 전용이며 canon을 변경하지 않는다. */
export async function readPart1ReadthroughStatusThroughTauri(
  invoke: TauriInvoke = defaultInvoke,
): Promise<Part1ReadthroughStatus> {
  return invoke<Part1ReadthroughStatus>('part1_readthrough_status');
}

// ── RAG 근거검색(⑤): 번들 canon을 의미 검색(Gemini 임베딩 + 로컬 코사인). 근거지 truth 아님(작가가 판단) ──
export interface RagHit {
  id: string;
  text: string;
  score: number;
}

/** 작가 질의 → 관련 canon top-k. Gemini 키(Keychain)/네트워크 실패면 reject(UI가 안내). */
export async function ragSearchThroughTauri(
  bundle: CanonBundleV1,
  query: string,
  scope?: string,
  k = 6,
  invoke: TauriInvoke = defaultInvoke,
): Promise<RagHit[]> {
  return invoke<RagHit[]>('rag_search', {
    bundleJson: JSON.stringify(bundle),
    query,
    scope: scope ?? null,
    k,
  });
}

// ── 작업실 독서실(W-R1): 원고 파일이 단일 진실. 장별 읽기·직접 손질·통독 메모. Dexie 경유 없음 ──
export interface ManuscriptChapter {
  heading: string; // ## 제외한 전체(예: "3장 — 빈 창", "에필로그 — 빈집")
  body: string;
}

export interface ManuscriptChaptersResult {
  path: string;
  headerMd: string; // 첫 장 앞의 원고 머리글(버전 계보 등)
  chapters: ManuscriptChapter[];
  mtimeMs: number; // 저장 시 낙관적 락 기준
}

/** 원고를 장별로 파싱해 읽는다. path 생략 시 백엔드 기본 원고(1부 병합본). 읽기 전용. */
export async function readManuscriptChaptersThroughTauri(
  path?: string,
  invoke: TauriInvoke = defaultInvoke,
): Promise<ManuscriptChaptersResult> {
  return invoke<ManuscriptChaptersResult>('read_manuscript_chapters', {
    ...(path ? { path } : {}),
  });
}

export interface SaveManuscriptChapterResult {
  mtimeMs: number; // 저장 후 새 mtime — 다음 저장의 baseMtimeMs
  logPath: string; // 장 단위 손질 로그 경로
}

/** 장 본문을 원고 파일에 직접 기록(손질 로그 append). baseMtimeMs 불일치면 백엔드가 한국어 에러로 reject. */
export async function saveManuscriptChapterThroughTauri(
  heading: string,
  body: string,
  baseMtimeMs: number,
  path?: string,
  invoke: TauriInvoke = defaultInvoke,
): Promise<SaveManuscriptChapterResult> {
  return invoke<SaveManuscriptChapterResult>('save_manuscript_chapter', {
    heading,
    body,
    baseMtimeMs,
    ...(path ? { path } : {}),
  });
}

/** 장별 통독 메모를 로그에 append. 반환은 로그 경로. 원고 본문은 건드리지 않는다. */
export async function appendReadingNoteThroughTauri(
  heading: string,
  note: string,
  invoke: TauriInvoke = defaultInvoke,
): Promise<string> {
  return invoke<string>('append_reading_note', { heading, note });
}

// ── 작업실 서재(W-R3): 캐논 문서를 읽기 전용으로 렌더. 허용목록 밖 경로는 백엔드가 문자열 에러 ──
/** 레포 문서(캐논 시트·프레임워크 등)를 마크다운 원문으로 읽는다. 읽기 전용. */
export async function readRepoDocThroughTauri(
  path: string,
  invoke: TauriInvoke = defaultInvoke,
): Promise<string> {
  return invoke<string>('read_repo_doc', { path });
}
